import DocumentTemplate from '../../../../shared/documents/DocumentTemplate';
import { getAffairPartnerName } from '../affairFormatters';
import { planningFeasibilityClientLabel, planningScopeLabel, PLANNING_CLIENT_SOURCE_LABELS } from '../affairPlanningLabels';
import { buildPlanningGanttBar, buildPlanningGanttTimeline, planningDateToUtcDate } from '../planningGanttTimeline';
import { buildPlanningGanttRows } from '../planningOrder';

const DAY_MS = 24 * 60 * 60 * 1000;
const SOURCE_LABELS = PLANNING_CLIENT_SOURCE_LABELS;

const formatDate = (value) => {
  const date = value instanceof Date ? value : planningDateToUtcDate(value);
  return date && !Number.isNaN(date.getTime())
    ? new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(date)
    : '—';
};
const formatMonth = (value) => new Intl.DateTimeFormat('fr-FR', { month: 'short', year: '2-digit', timeZone: 'UTC' }).format(value).replace('.', '');

const buildTimeline = (items, fallbackStart, fallbackEnd) => {
  const timeline = buildPlanningGanttTimeline(items, fallbackStart, fallbackEnd);
  if (!timeline) return null;
  const min = planningDateToUtcDate(timeline.minDate);
  const max = planningDateToUtcDate(timeline.maxDate);

  const months = [];
  let cursor = new Date(Date.UTC(min.getUTCFullYear(), min.getUTCMonth(), 1));
  while (cursor <= max) {
    const monthStart = new Date(Math.max(cursor.getTime(), min.getTime()));
    const nextMonth = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1));
    const monthEnd = new Date(Math.min(nextMonth.getTime() - DAY_MS, max.getTime()));
    const left = (timeline.position(monthStart) / timeline.spanDays) * 100;
    const width = (Math.max(1, timeline.position(monthEnd) - timeline.position(monthStart) + 1) / timeline.spanDays) * 100;
    months.push({ key: cursor.toISOString(), label: formatMonth(cursor), left, width });
    cursor = nextMonth;
  }

  return { ...timeline, min, max, months };
};

export default function AffairPlanningGanttDocument({ affair, items, fallbackStart, fallbackEnd, comparisons = [], summary = {}, phaseOrder = [] }) {
  const ganttRows = buildPlanningGanttRows(items, phaseOrder);
  const timeline = buildTimeline(ganttRows, fallbackStart, fallbackEnd);
  const comparisonByKey = new Map((comparisons || []).map((item) => [item.itemKey, item]));
  const workItems = (items || []).filter((item) => item.itemType === 'work');
  const totalHours = workItems.reduce((sum, item) => sum + (Number(item.estimatedHours) || 0), 0);

  return <DocumentTemplate
    title="Planning prévisionnel AO / DCE"
    orientation="landscape"
    cover={false}
    metadata={{
      projectName: affair?.name,
      projectReference: affair?.code,
      client: getAffairPartnerName(affair),
      phase: 'AO / DCE',
      documentDateLabel: formatDate(new Date()),
    }}
  >
    <section className="planning-pdf-summary">
      <div><span>Affaire</span><strong>{affair?.code || '—'} · {affair?.name || '—'}</strong></div>
      <div><span>Client</span><strong>{getAffairPartnerName(affair)}</strong></div>
      <div><span>Période</span><strong>{timeline ? `${formatDate(timeline.min)} - ${formatDate(timeline.max)}` : '—'}</strong></div>
      <div><span>Charge ICC</span><strong>{totalHours.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} h</strong></div>
      <div><span>Faisabilité du planning</span><strong>{planningFeasibilityClientLabel(summary?.status, summary?.feasibilityLabel || summary?.statusLabel || 'Faisabilité à confirmer')}</strong></div>
    </section>

    {!timeline ? <p>Aucune date n’est renseignée dans le planning.</p> : <section className="planning-pdf-gantt">
      <div className="planning-pdf-legend">
        <span><i className="is-icc" /> Planning prévisionnel ICC</span>
        <span><i className="is-client" /> Planning de référence client</span>
        <span><i className="is-late" /> Échéance client dépassée</span>
        <span className="planning-pdf-period">{formatDate(timeline.min)} → {formatDate(timeline.max)}</span>
      </div>
      <div className="planning-pdf-head">
        <div className="planning-pdf-head__label">Livrable / jalon</div>
        <div className="planning-pdf-head__timeline">
          {timeline.months.map((month) => <span key={month.key} style={{ left: `${month.left}%`, width: `${month.width}%` }}>{month.label}</span>)}
        </div>
      </div>
      <div className="planning-pdf-rows">
        {ganttRows.filter((row) => row.ganttKind === 'phase' || row.startDate || row.endDate).map((item) => {
          const isPhase = item.ganttKind === 'phase';
          const bar = buildPlanningGanttBar(item, timeline);
          if (!bar) return <div className="planning-pdf-row planning-pdf-row--phase" key={item.clientKey}><div className="planning-pdf-row__label"><strong>{item.ganttLabel || item.title}</strong></div><div className="planning-pdf-row__track" /></div>;
          const { startDate: start, endDate: end, leftPercent: left } = bar;
          const width = item.itemType === 'milestone' ? 0 : bar.widthPercent;
          const comparison = comparisonByKey.get(item.clientKey);
          const isLate = comparison?.status === 'late';
          return <div className={`planning-pdf-row${isPhase ? ' planning-pdf-row--phase' : ''}`} key={item.clientKey}>
            <div className="planning-pdf-row__label">
              <strong>{item.ganttLabel || item.title || 'Sans titre'}</strong>
              <span>{[SOURCE_LABELS[item.source], item.phaseCode, item.lotCode, planningScopeLabel(item.scopeType, item.scopeCode, item.levelCode), item.assignedResourceName].filter(Boolean).join(' · ')}</span>
              {item.itemType === 'work' && <small>{Number(item.estimatedHours || 0).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} h · {formatDate(start)} → {formatDate(end)}</small>}
            </div>
            <div className="planning-pdf-row__track">
              {timeline.months.map((month) => <i className="planning-pdf-month-line" key={month.key} style={{ left: `${month.left}%` }} />)}
              {item.itemType === 'milestone'
                ? <span className={`planning-pdf-milestone is-${item.source || 'icc'}${isLate ? ' is-late' : ''}`} style={{ left: `${left}%` }} />
                : <span className={`planning-pdf-bar is-${item.source || 'icc'}${isLate ? ' is-late' : ''}`} style={{ left: `${left}%`, width: `${Math.max(width, 0.9)}%` }} />}
            </div>
          </div>;
        })}
      </div>
    </section>}
  </DocumentTemplate>;
}
